import React, { useEffect, useMemo, useState } from "react";
import SEO from "./SEO";

const Pricing = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [estimatesPerYear, setEstimatesPerYear] = useState(null);
  const [showRecommenderModal, setShowRecommenderModal] = useState(false);
  const [suggestedPlan, setSuggestedPlan] = useState(null);

  const PLAN_REDIRECT_MAP = {
    1: { plan: 1, code: "FLEX-199" },
    8: { plan: 8, code: "PRIME-MONTH-999" },
    9: { plan: 9, code: "PRIME-YEAR-2999" },
    10: { plan: 10, code: "UNLIMITED-9999" },
  };

  const track = (name, props = {}) => {
    if (typeof window !== "undefined" && window.clarity) {
      window.clarity("event", name, props);
    }
  };

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const apiUrl = process.env.REACT_APP_SUBSCRIPTIONS_PLANS_API_URL;
        if (!apiUrl) throw new Error("API URL not configured");

        const response = await fetch(apiUrl);
        if (!response.ok) throw new Error(`API error: ${response.status}`);

        const allPlans = await response.json();
        const activePlans = (allPlans || [])
          .filter((plan) => plan.isActive === true)
          .sort((a, b) => (a.totalPrice || 0) - (b.totalPrice || 0));

        setPlans(activePlans);
        setError(null);
      } catch (err) {
        console.error("Failed to load pricing plans:", err);
        setError(err.message || "Failed to load pricing plans");
        setPlans([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const getPlanKey = (plan) => {
    const name = (plan.planName || "").toLowerCase();
    const duration = (plan.planDuration || "").toLowerCase();

    if (name.includes("unlimited")) return "unlimited";
    if (name.includes("prime") && duration === "yearly") return "primeYearly";
    if (name.includes("prime") && duration === "monthly") return "primeMonthly";
    if (name.includes("flex")) return "starter";
    return "other";
  };

  const getDuration = (plan) => {
    const duration = (plan.planDuration || "").toLowerCase();
    if (duration === "yearly") return "yearly";
    if (duration === "lifetime") return "lifetime";
    return "monthly";
  };

  const orderedPlans = useMemo(() => {
    const order = {
      starter: 1,
      primeMonthly: 2,
      primeYearly: 3,
      unlimited: 4,
      other: 5,
    };

    return [...plans].sort((a, b) => {
      const aOrder = order[getPlanKey(a)] || 99;
      const bOrder = order[getPlanKey(b)] || 99;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return (a.totalPrice || 0) - (b.totalPrice || 0);
    });
  }, [plans]);

  const primaryPlans = useMemo(() => {
    const starter = orderedPlans.find((p) => getPlanKey(p) === "starter") || orderedPlans[0];
    const primeYearly = orderedPlans.find((p) => getPlanKey(p) === "primeYearly");
    const unlimited = orderedPlans.find((p) => getPlanKey(p) === "unlimited");

    return [starter, primeYearly, unlimited].filter(Boolean);
  }, [orderedPlans]);

  const handleChoosePlan = (plan) => {
    track("pricing_plan_click", {
      plan: plan.planName,
      id: plan.subscriptionId,
    });

    const baseUrl = process.env.REACT_APP_SIGNUP_URL;
    const planConfig = PLAN_REDIRECT_MAP[plan.subscriptionId];

    if (!baseUrl || !planConfig) {
      console.error("Invalid plan or mapping missing", plan);
      return;
    }

    window.location.href = `${baseUrl}?plan=${planConfig.plan}&code=${planConfig.code}`;
  };

  const handleSuggestPlan = () => {
    const num = parseInt(inputValue, 10);
    if (!num || num <= 0) return;

    setEstimatesPerYear(num);

    const suggestion = num >= 25 ? "Prime Yearly" : "Prime Monthly";
    setSuggestedPlan(suggestion);

    track("pricing_plan_suggestion", {
      estimatesPerYear: num,
      suggestedPlan: suggestion,
    });
  };

  const getCardCopy = (plan) => {
    const key = getPlanKey(plan);
    const duration = getDuration(plan);
    const price = plan.totalPrice || 0;
    const credits = plan.totalCredits || 0;
    const pricePerCredit = credits ? price / credits : 0;

    if (key === "unlimited") {
      return {
        badge: "Premium Lifetime Plan",
        title: "Unlimited Lifetime",
        eyebrow: "For serious engineers, contractors & consultants",
        priceLabel: `₹${price.toLocaleString("en-IN")}`,
        durationLabel: "one-time",
        highlight: "Unlimited Estimates",
        subHighlight: "Many more premium features included",
        cta: "Go Unlimited",
        tone: "premium",
        features: [
          "Unlimited estimates",
          "Unlimited project usage",
          "All premium report features",
          "Lifetime validity",
          "Priority support",
        ],
      };
    }

    if (key === "primeYearly") {
      return {
        badge: "Most Recommended",
        title: "Prime Yearly",
        eyebrow: "Best value for regular estimation work",
        priceLabel: `₹${price.toLocaleString("en-IN")}`,
        durationLabel: "/year",
        highlight: "₹1 per estimate",
        subHighlight: "Applicable with Prime Yearly plan purchase",
        cta: "Get Prime Yearly",
        tone: "recommended",
        features: [
          `${credits.toLocaleString("en-IN")} total credits`,
          `Extra credits @ ₹${plan.extraCreditPrice || 0}/credit`,
          `${plan.creditExpirationDays || 365} days validity`,
          "Credit rollover on renewal",
          "Priority support",
        ],
      };
    }

    if (key === "starter") {
      return {
        badge: "Start Simple",
        title: plan.planName || "Starter",
        eyebrow: "For first-time users and occasional estimates",
        priceLabel: `₹${price.toLocaleString("en-IN")}`,
        durationLabel: duration === "yearly" ? "/year" : "/month",
        highlight: credits ? `${credits.toLocaleString("en-IN")} credits` : "Flexible credits",
        subHighlight: credits ? `Approx. ₹${pricePerCredit.toFixed(2)} per credit` : "Pay based on your need",
        cta: "Start Now",
        tone: "starter",
        features: [
          `${credits.toLocaleString("en-IN")} total credits`,
          `${plan.creditExpirationDays || 30} days validity`,
          "Professional estimate reports",
          "No long-term commitment",
          "Upgrade anytime",
        ],
      };
    }

    if (key === "primeMonthly") {
      return {
        badge: "Flexible Monthly",
        title: "Prime Monthly",
        eyebrow: "Good for short-term active usage",
        priceLabel: `₹${price.toLocaleString("en-IN")}`,
        durationLabel: "/month",
        highlight: credits ? `${credits.toLocaleString("en-IN")} credits` : "Monthly usage",
        subHighlight: credits ? `Approx. ₹${pricePerCredit.toFixed(2)} per credit` : "Flexible monthly billing",
        cta: "Choose Monthly",
        tone: "standard",
        features: [
          `${credits.toLocaleString("en-IN")} total credits`,
          `Extra credits @ ₹${plan.extraCreditPrice || 0}/credit`,
          `${plan.creditExpirationDays || 30} days validity`,
          "Upgrade anytime",
          "Standard support",
        ],
      };
    }

    return {
      badge: duration === "yearly" ? "Yearly Plan" : "Monthly Plan",
      title: plan.planName || "Plan",
      eyebrow: duration === "yearly" ? "Annual usage plan" : "Flexible monthly usage",
      priceLabel: `₹${price.toLocaleString("en-IN")}`,
      durationLabel: duration === "yearly" ? "/year" : duration === "lifetime" ? "" : "/month",
      highlight: credits ? `${credits.toLocaleString("en-IN")} credits` : "Flexible usage",
      subHighlight: credits ? `Approx. ₹${pricePerCredit.toFixed(2)} per credit` : "Choose based on requirement",
      cta: "Choose Plan",
      tone: "standard",
      features: [
        `${credits.toLocaleString("en-IN")} total credits`,
        `Extra credits @ ₹${plan.extraCreditPrice || 0}/credit`,
        `${plan.creditExpirationDays || 30} days validity`,
        "Credit rollover where applicable",
        "Standard support",
      ],
    };
  };

  const renderCheck = (premium = false) => (
    <span
      className={`mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${
        premium ? "bg-amber-400 text-slate-950" : "bg-emerald-100 text-emerald-700"
      }`}
    >
      <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
        <path
          fillRule="evenodd"
          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
          clipRule="evenodd"
        />
      </svg>
    </span>
  );

  const renderEnterprisePlanCard = (plan, compact = false) => {
    const copy = getCardCopy(plan);
    const isPremium = copy.tone === "premium";
    const isRecommended = copy.tone === "recommended";

    return (
      <div
        key={plan.subscriptionId}
        className={`relative flex h-full flex-col overflow-hidden border transition-all duration-300 ${
          compact ? "rounded-2xl" : "rounded-3xl"
        } ${
          isPremium
            ? "border-amber-300 bg-slate-950 text-white shadow-2xl shadow-slate-900/30"
            : isRecommended
              ? "border-orange-300 bg-white shadow-2xl shadow-orange-200/60"
              : "border-slate-200 bg-white shadow-lg shadow-slate-200/60 hover:-translate-y-1 hover:shadow-xl"
        }`}
      >
        {isPremium && (
          <div className="absolute inset-0 opacity-25">
            <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-amber-400 blur-3xl" />
            <div className="absolute -bottom-20 left-0 h-56 w-56 rounded-full bg-blue-600 blur-3xl" />
          </div>
        )}

        <div
          className={`relative h-2 w-full ${
            isPremium
              ? "bg-gradient-to-r from-amber-300 via-orange-400 to-amber-500"
              : isRecommended
                ? "bg-gradient-to-r from-orange-500 via-amber-400 to-orange-500"
                : "bg-slate-200"
          }`}
        />

        <div className={`relative flex h-full flex-col ${compact ? "p-5" : "p-6 lg:p-7"}`}>
          <div className="mb-5 flex items-start justify-between gap-3">
            <div>
              <span
                className={`inline-flex rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${
                  isPremium
                    ? "bg-amber-300 text-slate-950"
                    : isRecommended
                      ? "bg-orange-100 text-orange-700"
                      : "bg-slate-100 text-slate-700"
                }`}
              >
                {copy.badge}
              </span>
              <h3
                className={`mt-4 font-extrabold ${compact ? "text-xl" : "text-2xl"} ${
                  isPremium ? "text-white" : "text-slate-950"
                }`}
              >
                {copy.title}
              </h3>
              <p className={`mt-2 text-sm leading-6 ${isPremium ? "text-slate-300" : "text-slate-500"}`}>
                {copy.eyebrow}
              </p>
            </div>

            {isPremium && (
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-white/10 text-2xl">
                ∞
              </div>
            )}
            {isRecommended && (
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-orange-50 text-xl">
                ⭐
              </div>
            )}
          </div>

          <div className="mb-5">
            <div className="flex items-end gap-2">
              <span
                className={`${compact ? "text-3xl" : "text-4xl"} font-black tracking-tight ${
                  isPremium ? "text-white" : "text-slate-950"
                }`}
              >
                {copy.priceLabel}
              </span>
              <span className={`pb-1 text-sm font-semibold ${isPremium ? "text-slate-300" : "text-slate-500"}`}>
                {copy.durationLabel}
              </span>
            </div>

            <div
              className={`mt-4 rounded-2xl px-4 py-3 ${
                isPremium
                  ? "border border-amber-300/40 bg-white/10"
                  : isRecommended
                    ? "bg-orange-50 ring-1 ring-orange-200"
                    : "bg-slate-50"
              }`}
            >
              <p className={`text-lg font-extrabold ${isPremium ? "text-amber-200" : "text-slate-950"}`}>
                {copy.highlight}
              </p>
              <p className={`mt-1 text-xs ${isPremium ? "text-slate-300" : "text-slate-500"}`}>
                {copy.subHighlight}
              </p>
            </div>
          </div>

          <div className="mb-6 space-y-3">
            {copy.features.map((feature) => (
              <div key={feature} className="flex gap-3 text-sm">
                {renderCheck(isPremium)}
                <span className={isPremium ? "text-slate-200" : "text-slate-700"}>{feature}</span>
              </div>
            ))}
          </div>

          <button
            onClick={() => handleChoosePlan(plan)}
            className={`mt-auto w-full rounded-2xl px-5 py-3 text-sm font-extrabold transition-all duration-300 ${
              isPremium
                ? "bg-amber-300 text-slate-950 shadow-lg shadow-amber-500/30 hover:bg-amber-200"
                : isRecommended
                  ? "bg-gradient-to-r from-orange-600 to-amber-500 text-white shadow-lg shadow-orange-500/30 hover:from-orange-700 hover:to-amber-600"
                  : "bg-slate-950 text-white hover:bg-slate-800"
            }`}
          >
            {copy.cta}
          </button>
        </div>
      </div>
    );
  };

  const trainingBundlePlan = {
    planName: "Government Estimation Training + 1 Year myBOQ",
    totalPrice: 5900,
    partner: "CEIIT",
    features: [
      "1 Year myBOQ subscription",
      "Government estimation training",
      "SSR estimate tutorials",
      "Certificate of completion",
      "10 estimates in PDF for reference",
    ],
  };

  const renderTrainingBundleCard = () => (
    <div className="rounded-3xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-orange-50 p-6 shadow-lg shadow-slate-200/70">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <span className="inline-flex rounded-full bg-blue-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-blue-700">
            Training Bundle
          </span>
          <h3 className="mt-3 text-2xl font-extrabold text-slate-950">{trainingBundlePlan.planName}</h3>
          <p className="mt-2 text-sm text-slate-600">Training Partner: {trainingBundlePlan.partner}</p>
        </div>

        <div className="text-left lg:text-right">
          <p className="text-4xl font-black text-slate-950">
            ₹{trainingBundlePlan.totalPrice.toLocaleString("en-IN")}
          </p>
          <p className="mt-1 text-sm font-semibold text-orange-700">Includes Prime Yearly benefits</p>
        </div>
      </div>

      <div className="mt-6 grid gap-3 md:grid-cols-2 lg:grid-cols-5">
        {trainingBundlePlan.features.map((feature) => (
          <div key={feature} className="flex items-start gap-2 rounded-2xl bg-white/80 p-3 text-sm text-slate-700 shadow-sm">
            {renderCheck(false)}
            <span>{feature}</span>
          </div>
        ))}
      </div>

      <button
        onClick={() => {
          const baseUrl = process.env.REACT_APP_SIGNUP_URL;
          if (!baseUrl) return;
          window.location.href = `${baseUrl}?plan=training&code=TRAINING-5900`;
        }}
        className="mt-6 rounded-2xl bg-blue-700 px-6 py-3 text-sm font-extrabold text-white shadow-lg shadow-blue-500/30 hover:bg-blue-800"
      >
        Choose Training Bundle
      </button>
    </div>
  );

  const resetRecommender = () => {
    setShowRecommenderModal(false);
    setEstimatesPerYear(null);
    setSuggestedPlan(null);
    setInputValue("");
  };

  return (
    <>
      <SEO
        title="Pricing | myBOQ Civil Estimation Plans"
        description="Compare myBOQ pricing plans. Start with flexible credits, choose Prime Yearly for ₹1 per estimate, or unlock Unlimited Lifetime access."
        keywords="myBOQ pricing, civil estimation subscription, BOQ software plans, construction estimate software"
        image="https://myboq.siliconmount.com/app/subscription.png"
        url="https://myboq.siliconmount.com/pricing"
      />

      <main className="min-h-screen bg-slate-50">
        <section className="relative overflow-hidden bg-slate-950 px-4 py-14 text-white sm:py-16 lg:py-20">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-orange-500 blur-3xl" />
            <div className="absolute bottom-0 right-10 h-72 w-72 rounded-full bg-blue-600 blur-3xl" />
          </div>

          <div className="relative mx-auto max-w-7xl text-center">
            <span className="inline-flex rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-orange-200">
              myBOQ Pricing
            </span>
            <h1 className="mx-auto mt-6 max-w-4xl text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">
              Start at ₹1 per estimate. Scale to Unlimited.
            </h1>
            <p className="mx-auto mt-5 max-w-3xl text-base leading-8 text-slate-300 sm:text-lg">
              Choose from all available plans without confusion — organized clearly for engineers, contractors, consultants, and infrastructure estimation teams.
            </p>

            <div className="mx-auto mt-8 grid max-w-4xl gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                <p className="text-2xl font-black text-white">₹1</p>
                <p className="text-sm text-slate-300">Per estimate with Prime Yearly</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                <p className="text-2xl font-black text-white">Unlimited</p>
                <p className="text-sm text-slate-300">Estimates + premium features</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                <p className="text-2xl font-black text-white">All Plans</p>
                <p className="text-sm text-slate-300">Shown clearly below</p>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 py-10 lg:py-14">
          <div className="mx-auto max-w-7xl">
            <div className="mb-8 grid gap-4 lg:grid-cols-[1.3fr_0.7fr]">
              <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5 shadow-sm">
                <div className="flex gap-3">
                  {renderCheck(false)}
                  <div>
                    <h2 className="text-lg font-extrabold text-emerald-950">No subscription needed for signup</h2>
                    <p className="mt-1 text-sm leading-6 text-emerald-800">
                      Create an account and explore myBOQ. Buy a plan only when you see the value and need more credits.
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowRecommenderModal(true)}
                className="rounded-3xl border border-orange-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg"
              >
                <p className="text-xs font-bold uppercase tracking-wide text-orange-600">Smart Recommendation</p>
                <h2 className="mt-2 text-lg font-extrabold text-slate-950">Find your perfect plan →</h2>
                <p className="mt-1 text-sm text-slate-600">Answer one question and get a plan suggestion.</p>
              </button>
            </div>

            {loading && (
              <div className="rounded-3xl bg-white p-12 text-center shadow-sm">
                <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-orange-200 border-t-orange-600" />
                <p className="mt-4 text-slate-600">Loading plans...</p>
              </div>
            )}

            {error && (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">
                Error loading pricing plans: {error}
              </div>
            )}

            {!loading && orderedPlans.length > 0 && (
              <>
                <div className="mb-8 text-center">
                  <h2 className="text-3xl font-black text-slate-950">Choose the right plan</h2>
                  <p className="mt-2 text-slate-600">All plans are visible, but organized in a clean enterprise layout.</p>
                </div>

                <div className="mb-10">
                  <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-orange-600">Recommended Choices</p>
                      <h3 className="mt-1 text-2xl font-black text-slate-950">Fast decision plans</h3>
                    </div>
                    <p className="max-w-xl text-sm leading-6 text-slate-600">
                      These are highlighted first for quick decision making: Start Simple, Prime Yearly, and Unlimited Lifetime.
                    </p>
                  </div>

                  <div className="grid gap-6 lg:grid-cols-3 lg:items-stretch">{primaryPlans.map((plan) => renderEnterprisePlanCard(plan))}</div>
                </div>

                <div className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-xl shadow-slate-200/60 sm:p-6 lg:p-8">
                  <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Complete Pricing Menu</p>
                      <h3 className="mt-1 text-2xl font-black text-slate-950">All available plans</h3>
                      <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
                        Every active plan is shown here in a compact, clean layout. This avoids hiding options while keeping the page premium and readable.
                      </p>
                    </div>

                    <button
                      onClick={() => setShowRecommenderModal(true)}
                      className="rounded-2xl bg-slate-950 px-5 py-3 text-sm font-extrabold text-white shadow-lg shadow-slate-300/60 transition hover:bg-slate-800"
                    >
                      Help Me Choose
                    </button>
                  </div>

                  <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">{orderedPlans.map((plan) => renderEnterprisePlanCard(plan, true))}</div>
                </div>

                <div className="mt-10">{renderTrainingBundleCard()}</div>
              </>
            )}

            {!loading && orderedPlans.length === 0 && !error && (
              <div className="rounded-3xl bg-white p-12 text-center shadow-sm">
                <p className="text-slate-600">No active plans available</p>
              </div>
            )}
          </div>
        </section>

        {showRecommenderModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
            <div className="relative w-full max-w-md rounded-3xl bg-white p-7 shadow-2xl">
              <button
                onClick={resetRecommender}
                className="absolute right-4 top-4 rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                aria-label="Close"
              >
                ✕
              </button>

              {!suggestedPlan ? (
                <>
                  <p className="text-xs font-bold uppercase tracking-wide text-orange-600">Plan Finder</p>
                  <h2 className="mt-2 text-2xl font-black text-slate-950">How many estimates do you create in a year?</h2>
                  <p className="mt-2 text-sm leading-6 text-slate-600">We’ll suggest the best plan based on your usage.</p>

                  <input
                    type="number"
                    min="1"
                    max="9999"
                    placeholder="Example: 24"
                    autoFocus
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    className="mt-6 w-full rounded-2xl border-2 border-slate-200 px-4 py-3 text-lg outline-none transition focus:border-orange-500 focus:ring-4 focus:ring-orange-100"
                  />

                  <button
                    onClick={handleSuggestPlan}
                    disabled={!inputValue || Number(inputValue) <= 0}
                    className="mt-4 w-full rounded-2xl bg-orange-600 px-5 py-3 font-extrabold text-white transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                  >
                    Suggest Plan
                  </button>
                </>
              ) : (
                <div className="text-center">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-emerald-100 text-3xl">✓</div>
                  <p className="mt-5 text-sm font-bold uppercase tracking-wide text-slate-500">We recommend</p>
                  <h3 className="mt-2 text-3xl font-black text-orange-600">{suggestedPlan}</h3>
                  <p className="mt-4 rounded-2xl bg-slate-50 p-4 text-left text-sm leading-6 text-slate-700">
                    With <strong>{estimatesPerYear}</strong> estimates/year, {suggestedPlan} is the better fit for your current usage.
                  </p>
                  <button onClick={resetRecommender} className="mt-5 w-full rounded-2xl bg-slate-950 px-5 py-3 font-extrabold text-white hover:bg-slate-800">
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </>
  );
};

export default Pricing;
